import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";

/**
 * VerifyEmail — landing page for the emailed verification link.
 * Route: `/verify?token=…`
 *
 * The mobile-team email template points at
 *   https://mytabletopapp.co.uk/verify?token=<opaque>
 * so we can own the branded landing (no more expo preview URL) and only
 * hand the token off to the API. Success → cheer + "Continue to Dashboard".
 * Expired/invalid → friendly copy + "Resend from Dashboard" CTA.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "loading" : "missing"); // loading | success | error | missing
  const [message, setMessage] = useState("");
  // StrictMode-safe: guarantee the token is consumed by the API exactly once
  // per page load. Without this, single-use tokens would be invalidated by
  // the second dev-mode effect invocation.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email`, { params: { token } });
        if (data?.ok) {
          setStatus("success");
          setMessage(data.message || "Email verified.");
        } else {
          setStatus("error");
          setMessage(data?.message || "This verification link is invalid or has expired.");
        }
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "Could not reach the verification service. Please try again shortly.");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="verify-email-page">
      <Navbar />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="card-base p-8 text-center">
          <div className="pill bg-brandTeal/15 text-brandTeal mx-auto inline-flex">
            <ShieldCheck className="w-3 h-3 mr-1" /> Email verification
          </div>

          {status === "loading" && (
            <div className="mt-6" data-testid="verify-loading">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-brandTeal" />
              <h1 className="font-display text-2xl font-semibold text-brandSlate mt-4">
                Confirming your email&hellip;
              </h1>
              <p className="text-sm text-slate-500 mt-1">This only takes a moment.</p>
            </div>
          )}

          {status === "success" && (
            <div className="mt-6" data-testid="verify-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-semibold text-brandSlate mt-4">
                You&rsquo;re verified!
              </h1>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Thanks for confirming your email. Your MyTableTopApp account is ready to go &mdash; payout notifications and password resets will now reach you.
              </p>
              <Link to="/dashboard/parent" className="btn-primary mt-6 inline-flex" data-testid="verify-continue-btn">
                Continue to Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {(status === "error" || status === "missing") && (
            <div className="mt-6" data-testid="verify-error">
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 text-rose-700 inline-flex items-center justify-center">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-semibold text-brandSlate mt-4">
                Hmm, that link didn&rsquo;t work
              </h1>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                {status === "missing"
                  ? "No verification token found in the link. Please tap the button in your latest email."
                  : message}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                <Link to="/dashboard/parent" className="btn-primary" data-testid="verify-resend-cta">
                  Resend from Dashboard
                </Link>
                <Link to="/" className="btn-secondary" data-testid="verify-home-btn">
                  Back to home
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
